import React from 'react';

/**
 * RichTextView — read-only renderer for TipTap/ProseMirror JSON.
 *
 * WHY THIS EXISTS RATHER THAN MOUNTING AN EDITOR OR SETTING innerHTML.
 *
 * Two obvious alternatives were rejected for the CUSTOMER surface specifically:
 *
 *   1. Mount a TipTap editor with `editable: false`. Correct output, but the
 *      customer bundle does not import TipTap today (verified 2026-08-04: zero
 *      tiptap references under src/pages/CustomerPortal/ and src/components/
 *      Portal/), and pulling the editor + StarterKit onto a parent-facing route
 *      to render static paragraphs is a large amount of JavaScript for a page
 *      most families will open on a phone.
 *
 *   2. generateHTML() + dangerouslySetInnerHTML. This renders teacher-authored
 *      content, which is not attacker-controlled in any ordinary sense — but it
 *      is content written by one person and rendered in another household's
 *      browser, and the whole arc's premise is a hard wall between what staff
 *      write and what families see. Choosing the one rendering path that can
 *      execute markup is a bad trade for formatting we can walk explicitly.
 *
 * So: an explicit node walk producing React elements. Unknown node types render
 * their children rather than disappearing — a recap must never silently lose a
 * teacher's words because someone enabled a new StarterKit extension upstream.
 *
 * MENTIONS RENDER AS PLAIN TEXT, NOT CHIPS. A chip implies a link to an entity
 * a family cannot open, and the mention index (documents.studentIds) can name
 * children of OTHER families — being mentioned grants nothing, so it must not
 * look interactive here.
 */

const MARK_WRAPPERS = {
	bold: (child, key) => <strong key={key}>{child}</strong>,
	strong: (child, key) => <strong key={key}>{child}</strong>,
	italic: (child, key) => <em key={key}>{child}</em>,
	em: (child, key) => <em key={key}>{child}</em>,
	underline: (child, key) => <u key={key}>{child}</u>,
	strike: (child, key) => <s key={key}>{child}</s>,
	code: (child, key) => (
		<code key={key} style={{ fontSize: '0.92em', background: 'rgba(0,0,0,0.05)', padding: '0.1em 0.3em', borderRadius: 3 }}>
			{child}
		</code>
	),
	highlight: (child, key) => (
		<mark key={key} style={{ background: 'var(--cyv-highlight, #fff3bf)', padding: '0 0.1em' }}>
			{child}
		</mark>
	),
};

const applyMarks = (text, marks, key) => {
	if (!marks || !marks.length) return text;
	return marks.reduce((acc, mark, i) => {
		const wrap = MARK_WRAPPERS[mark?.type];
		return wrap ? wrap(acc, `${key}-m${i}`) : acc;
	}, text);
};

const alignStyle = (node) =>
	node?.attrs?.textAlign ? { textAlign: node.attrs.textAlign } : undefined;

const renderNode = (node, key) => {
	if (!node || typeof node !== 'object') return null;

	const children = (node.content || []).map((child, i) => renderNode(child, `${key}.${i}`));

	switch (node.type) {
		case 'text':
			return applyMarks(node.text ?? '', node.marks, key);

		case 'hardBreak':
			return <br key={key} />;

		case 'paragraph':
			// An empty paragraph is a deliberate blank line in the teacher's
			// writing; render it with height rather than collapsing it away.
			return (
				<p key={key} style={{ margin: '0 0 0.7em', lineHeight: 1.6, ...alignStyle(node) }}>
					{children.length ? children : ' '}
				</p>
			);

		case 'heading': {
			const level = Math.min(Math.max(Number(node.attrs?.level) || 2, 1), 6);
			// Headings are shifted down one level: the surrounding page already
			// owns h1/h2, and a recap's own heading must not outrank the
			// student's name in the document outline.
			const Tag = `h${Math.min(level + 2, 6)}`;
			return (
				<Tag key={key} style={{ margin: '0.9em 0 0.4em', fontSize: '1rem', fontWeight: 700, ...alignStyle(node) }}>
					{children}
				</Tag>
			);
		}

		case 'bulletList':
			return <ul key={key} style={{ margin: '0 0 0.7em', paddingLeft: '1.3em' }}>{children}</ul>;

		case 'orderedList':
			return (
				<ol key={key} start={node.attrs?.start || 1} style={{ margin: '0 0 0.7em', paddingLeft: '1.4em' }}>
					{children}
				</ol>
			);

		case 'listItem':
			return <li key={key} style={{ margin: '0.15em 0' }}>{children}</li>;

		case 'taskList':
			return <ul key={key} style={{ margin: '0 0 0.7em', paddingLeft: '1.2em', listStyle: 'none' }}>{children}</ul>;

		case 'taskItem':
			return (
				<li key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0.2em 0' }}>
					<input type='checkbox' checked={!!node.attrs?.checked} readOnly aria-hidden='true' style={{ marginTop: '0.35em' }} />
					<span>{children}</span>
				</li>
			);

		case 'blockquote':
			return (
				<blockquote
					key={key}
					style={{ margin: '0 0 0.7em', padding: '0.1em 0 0.1em 0.9em', borderLeft: '3px solid var(--cyv-teal-600, #54ada7)', color: 'var(--text-muted, #5c564e)' }}>
					{children}
				</blockquote>
			);

		case 'codeBlock':
			return (
				<pre key={key} style={{ margin: '0 0 0.7em', padding: '0.7em 0.9em', background: 'rgba(0,0,0,0.05)', borderRadius: 6, overflowX: 'auto', fontSize: '0.88rem' }}>
					<code>{children}</code>
				</pre>
			);

		case 'horizontalRule':
			return <hr key={key} style={{ border: 0, borderTop: '1px solid var(--border-subtle, #e3ded5)', margin: '1em 0' }} />;

		case 'mention':
			// Plain text, deliberately — see the header note.
			return <span key={key}>{node.attrs?.label || node.attrs?.id || ''}</span>;

		case 'doc':
			return <React.Fragment key={key}>{children}</React.Fragment>;

		default:
			// Unknown node: render what is inside it rather than dropping a
			// teacher's words on the floor.
			return <React.Fragment key={key}>{children}</React.Fragment>;
	}
};

/**
 * @param {object} content - TipTap JSON (the `content` field of a recap doc)
 * @param {string} fallback - plainText, used when the JSON is missing/unusable
 */
const RichTextView = ({ content, fallback = '' }) => {
	const hasJson = content && typeof content === 'object' && (content.content || []).length > 0;

	if (!hasJson) {
		if (!fallback || !fallback.trim()) return null;
		// plainText carries no structure; preserve the line breaks the teacher
		// typed rather than running the whole recap into one paragraph.
		return (
			<div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, textAlign: 'left' }}>{fallback}</div>
		);
	}

	// textAlign IS LOAD-BEARING, and it is not defensive boilerplate. App.js
	// puts `text-center` on the outermost wrapper (App.js:151) and the portal
	// shell renders INSIDE it — customerShell.css contains no text-align reset
	// at all. Every existing row on the customer Schedule page escapes that only
	// because it is a flex container; recap prose is the first block-level text
	// on the page, so without this line paragraphs and bullets render centred.
	// Measured in a real browser 2026-08-04; jsdom computes no layout and no
	// test here can see it. Per-node alignment from the TipTap text-align
	// extension still wins, because that lands as an inline style on the node.
	// fontWeight is the SAME finding as textAlign, from the same global rule:
	// App.css:20 sets `body, html { font-family:'Josefin Sans'!important;
	// font-weight:300 }`, and 300 of a geometric display face is thin for dense
	// prose. A recap is the longest continuous text a family reads in the
	// portal. Weight and leading only — the family stays, that is a brand call.
	return (
		<div className='cyv-richtext' style={{ textAlign: 'left', fontWeight: 400, lineHeight: 1.65 }}>
			{renderNode(content, 'r')}
		</div>
	);
};

export default RichTextView;
